/**
 * @mellify/shared - Role Permissions
 *
 * Defines granular permissions for each workspace member role.
 * This is the single source of truth used by both frontend and backend
 * for permission checks.
 */
/**
 * Human-readable labels for each permission.
 */
export const PERMISSION_LABELS = {
    manage_team: 'Manage Team',
    manage_channels: 'Manage Channels',
    manage_kb: 'Manage Knowledge Base',
    create_content: 'Create Content',
    approve_content: 'Approve Content',
    manage_campaigns: 'Manage Campaigns',
    view_analytics: 'View Analytics',
    manage_settings: 'Manage Settings',
    manage_billing: 'Manage Billing',
    manage_ads: 'Manage Ads',
    view_ad_insights: 'View Ad Insights',
};
/**
 * Descriptions for each permission.
 */
export const PERMISSION_DESCRIPTIONS = {
    manage_team: 'Invite, remove, and change roles of team members',
    manage_channels: 'Connect and manage social media accounts',
    manage_kb: 'Upload, edit, and manage knowledge base documents',
    create_content: 'Create and edit content posts and media',
    approve_content: 'Review and approve content before publishing',
    manage_campaigns: 'Create, edit, and manage marketing campaigns',
    view_analytics: 'Access analytics dashboards and reports',
    manage_settings: 'Configure workspace settings and preferences',
    manage_billing: 'Manage subscription, billing, and invoices',
    manage_ads: 'Create, edit, and manage ad campaigns, ad sets, ads, and creatives',
    view_ad_insights: 'View ad performance metrics, spend, and insights',
};
/**
 * All available permissions in display order.
 */
export const ALL_PERMISSIONS = [
    'manage_team',
    'manage_channels',
    'manage_kb',
    'create_content',
    'approve_content',
    'manage_campaigns',
    'view_analytics',
    'manage_settings',
    'manage_billing',
    'manage_ads',
    'view_ad_insights',
];
/**
 * Permission matrix mapping each role to its granted permissions.
 */
export const ROLE_PERMISSIONS = {
    owner: [
        'manage_team',
        'manage_channels',
        'manage_kb',
        'create_content',
        'approve_content',
        'manage_campaigns',
        'view_analytics',
        'manage_settings',
        'manage_billing',
        'manage_ads',
        'view_ad_insights',
    ],
    admin: [
        'manage_team',
        'manage_channels',
        'manage_kb',
        'create_content',
        'approve_content',
        'manage_campaigns',
        'view_analytics',
        'manage_settings',
        'manage_ads',
        'view_ad_insights',
    ],
    manager: [
        'manage_channels',
        'manage_kb',
        'create_content',
        'manage_campaigns',
        'view_analytics',
        'manage_ads',
        'view_ad_insights',
    ],
    approver: [
        'create_content',
        'approve_content',
    ],
    analyst: [
        'view_analytics',
        'view_ad_insights',
    ],
    support: [],
};
/**
 * Check whether a given role has a specific permission.
 */
export function roleHasPermission(role, permission) {
    return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}
/**
 * Get all permissions granted to a role.
 */
export function getPermissionsForRole(role) {
    return ROLE_PERMISSIONS[role] ?? [];
}
/**
 * Compare permissions between two roles.
 * Returns which permissions are gained and lost when changing from one role to another.
 */
export function compareRolePermissions(fromRole, toRole) {
    const fromPerms = new Set(ROLE_PERMISSIONS[fromRole] ?? []);
    const toPerms = new Set(ROLE_PERMISSIONS[toRole] ?? []);
    const gained = [];
    const lost = [];
    const retained = [];
    for (const perm of ALL_PERMISSIONS) {
        const hadPerm = fromPerms.has(perm);
        const willHavePerm = toPerms.has(perm);
        if (hadPerm && willHavePerm) {
            retained.push(perm);
        }
        else if (!hadPerm && willHavePerm) {
            gained.push(perm);
        }
        else if (hadPerm && !willHavePerm) {
            lost.push(perm);
        }
    }
    return { gained, lost, retained };
}
/**
 * Roles that are assignable (owner cannot be assigned via role change).
 */
export const ASSIGNABLE_ROLES = [
    'admin',
    'manager',
    'approver',
    'analyst',
    'support',
];
/**
 * Human-readable labels for each role.
 */
export const ROLE_LABELS = {
    owner: 'Owner',
    admin: 'Admin',
    manager: 'Manager',
    approver: 'Approver',
    analyst: 'Analyst',
    support: 'Support',
};
/**
 * Descriptions for each role.
 */
export const ROLE_DESCRIPTIONS = {
    owner: 'Full control over the workspace, billing, and all settings',
    admin: 'Full workspace management except billing',
    manager: 'Manage content, campaigns, channels, knowledge base, and ads',
    approver: 'Review and approve content before publishing',
    analyst: 'View analytics dashboards, reports, and ad insights',
    support: 'Handle engagement and customer support',
};
//# sourceMappingURL=role-permissions.js.map